import type { Room } from "building-types/rooms/roomTypes";
import type { RoomType } from "building-types/rooms/roomTypesEnum";
import type { FloorAreaTakeoffConfig, RoomBorderPositions } from "building-types/rooms/takeoffTypes";
import { AreaRuleType, BorderPosition } from "building-types/rooms/takeoffTypes";
import { WallType } from "building-types/walls/wallEnums";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { WallPath } from "building-types/walls/wallTypes";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { isRoomSelected } from "doc/utils/floorAreaTakeoffTools";
import type { PlanarGraphLoops } from "geom/planarGraph";
import { getWallPath } from "../walls/wall";
import { solidWallFramingLayer } from "../walls/wallLayers";
import type { RoomBoundaryTable } from "./getIncludedAndExcludedRooms";

interface EdgeOffsetInfo {
  readonly borderOffsetDirLeft: boolean;
  readonly borderOffsetToFraming: boolean;
}

export function makePerimeterEdgeOffsetList(
  areaPerimeters: PlanarGraphLoops,
  levelWalls: WallSystem,
  takeOffConfig: FloorAreaTakeoffConfig,
  includedRoomIds: { [roomId: string]: boolean },
  roomBoundaryTable: RoomBoundaryTable,
) {
  const offsetList: number[][] = [];
  for (const hes of areaPerimeters.faceHalfEdges) {
    const offsets: number[] = [];
    for (const index of Object.keys(hes)) {
      const he = hes[index];
      const wall = levelWalls.walls[levelWalls.halfEdgeToWall[he]];
      const path = getWallPath(wall);
      const { borderOffsetToFraming, borderOffsetDirLeft } = getEdgeOffsetAndDir(
        takeOffConfig,
        path,
        roomBoundaryTable,
        includedRoomIds,
      );

      let offset = 0;

      if (borderOffsetToFraming) {
        switch (wall.wallType) {
          case WallType.SOLID:
          case WallType.SOLID_MASS: {
            const framingMass = solidWallFramingLayer(wall);
            offset = borderOffsetDirLeft ? framingMass.leftOffset : -framingMass.rightOffset;
            break;
          }
          case WallType.VOID: {
            break;
          }
        }

        if (path.halfEdgeIndex === he) {
          offset = -offset;
        }
      }

      offsets.push(offset);
      if (parseInt(index) === hes.length - 1) {
        offsets.push(offset);
      }
    }

    offsetList.push(offsets);
  }
  return offsetList;
}

function getEdgeOffsetAndDir(
  takeOffConfig: FloorAreaTakeoffConfig,
  path: WallPath,
  roomBoundaryTable: RoomBoundaryTable,
  includedRoomIds: { [roomId: string]: boolean },
) {
  const leftSideIsExterior = path.leftRoom === undefined;
  const rightSideIsExterior = path.rightRoom === undefined;
  const exteriorPath = leftSideIsExterior || rightSideIsExterior;

  if (leftSideIsExterior && rightSideIsExterior) {
    // should not occur
    return { borderOffsetToFraming: false, borderOffsetDirLeft: true };
  } else if (exteriorPath) {
    // Room Included to Exterior
    return getEdgeOffsetRoomIncludedAndExteriorBoundary(takeOffConfig, leftSideIsExterior, path, roomBoundaryTable);
  } else {
    // Room Included to Room Excluded boundary
    const includedIsLeft = includedRoomIds[path.leftRoom.id];

    const result = includeByRoom(takeOffConfig, includedIsLeft, path);
    if (result) {
      return { borderOffsetToFraming: result.borderOffsetToFraming, borderOffsetDirLeft: result.borderOffsetDirLeft };
    }

    const result2 = includeByRoomSetAssociation(includedIsLeft, path, roomBoundaryTable);
    if (result2) {
      return { borderOffsetToFraming: result2.borderOffsetToFraming, borderOffsetDirLeft: result2.borderOffsetDirLeft };
    }

    const result3 = excludeByRoom(takeOffConfig, includedIsLeft, path);
    if (result3) {
      return { borderOffsetToFraming: result3.borderOffsetToFraming, borderOffsetDirLeft: result3.borderOffsetDirLeft };
    }

    const result4 = excludeByRoomSetAssociation(includedIsLeft, path, roomBoundaryTable);
    if (result4) {
      return { borderOffsetToFraming: result4.borderOffsetToFraming, borderOffsetDirLeft: result4.borderOffsetDirLeft };
    }

    return { borderOffsetToFraming: false, borderOffsetDirLeft: true };
  }
}

function getEdgeOffsetRoomIncludedAndExteriorBoundary(
  takeOffConfig: FloorAreaTakeoffConfig,
  leftSideIsExterior: boolean,
  path: WallPath,
  roomBoundaryTable: RoomBoundaryTable,
) {
  for (const configInc of takeOffConfig.include) {
    const { exteriorRoomBorderPosition } = configInc;
    if (exteriorRoomBorderPosition) {
      const room = leftSideIsExterior ? path.rightRoom : path.leftRoom;

      if (!room) {
        continue;
      }

      switch (configInc.type) {
        case AreaRuleType.BY_ROOM_SELECTOR: {
          if (isRoomSelected(room, configInc.roomSelectors)) {
            return edgeOffsetInfoByBorderPosition(exteriorRoomBorderPosition, !leftSideIsExterior, false);
          }
          break;
        }
        case AreaRuleType.BY_ROOM_TYPE: {
          for (const roomType in configInc.roomTypes || {}) {
            if (room.type === roomType) {
              return edgeOffsetInfoByBorderPosition(exteriorRoomBorderPosition, !leftSideIsExterior, false);
            }
          }
          break;
        }
      }
    }
  }

  const roomId = leftSideIsExterior ? path.rightRoom?.id : path.leftRoom?.id;
  const result = roomId ? roomBoundaryTable[roomId] : undefined;

  if (result?.exteriorRoomBorderPosition) {
    return edgeOffsetInfoByBorderPosition(result.exteriorRoomBorderPosition, !leftSideIsExterior, false);
  }

  return { borderOffsetDirLeft: leftSideIsExterior, borderOffsetToFraming: true };
}

function includeByRoom(
  takeOffConfig: FloorAreaTakeoffConfig,
  includedIsLeft: boolean,
  path: WallPath,
): EdgeOffsetInfo | null {
  for (const configInc of takeOffConfig.include) {
    const { otherRoomBorderPosition, specificRoomBorderPositions } = configInc;
    const otherRoom = !includedIsLeft ? path.leftRoom : path.rightRoom;
    if (!otherRoom) {
      continue;
    }

    const otherRoomType = otherRoom.type;

    const result = specificOrOtherRoomBorderPositions(
      specificRoomBorderPositions,
      otherRoom,
      otherRoomType,
      otherRoomBorderPosition,
      includedIsLeft,
      false,
    );
    if (result) {
      return result;
    }
  }

  return null;
}

function includeByRoomSetAssociation(
  includedIsLeft: boolean,
  path: WallPath,
  roomBoundaryTable: RoomBoundaryTable,
): EdgeOffsetInfo | null {
  const roomId = includedIsLeft ? path.leftRoom?.id : path.rightRoom?.id;
  const roomBoundaryResult = roomId ? roomBoundaryTable[roomId] : undefined;

  if (roomBoundaryResult && roomBoundaryResult.includedRoom) {
    const otherRoom = !includedIsLeft ? path.leftRoom : path.rightRoom;
    assertIsNotNullOrUndefined(otherRoom);

    const otherRoomType = otherRoom.type;
    const specificRoomBorderPositions = roomBoundaryResult.specificRoomBorderPositions;
    const otherRoomBorderPosition = roomBoundaryResult.otherRoomBorderPosition;

    const result = specificOrOtherRoomBorderPositions(
      specificRoomBorderPositions,
      otherRoom,
      otherRoomType,
      otherRoomBorderPosition,
      includedIsLeft,
      false,
    );
    if (result) {
      return result;
    }
  }

  return null;
}

function excludeByRoom(
  takeOffConfig: FloorAreaTakeoffConfig,
  includedIsLeft: boolean,
  path: WallPath,
): EdgeOffsetInfo | null {
  for (const configExc of takeOffConfig.exclude) {
    const { otherRoomBorderPosition, specificRoomBorderPositions } = configExc;
    const otherRoom = !includedIsLeft ? path.rightRoom : path.leftRoom;
    assertIsNotNullOrUndefined(otherRoom);

    const otherRoomType = otherRoom.type;
    const result = specificOrOtherRoomBorderPositions(
      specificRoomBorderPositions,
      otherRoom,
      otherRoomType,
      otherRoomBorderPosition,
      includedIsLeft,
      true,
    );
    if (result) {
      return result;
    }
  }
  return null;
}

function excludeByRoomSetAssociation(
  includedIsLeft: boolean,
  path: WallPath,
  roomBoundaryTable: RoomBoundaryTable,
): EdgeOffsetInfo | null {
  const roomId = includedIsLeft ? path.rightRoom?.id : path.leftRoom?.id;
  const roomBoundaryResult = roomId ? roomBoundaryTable[roomId] : undefined;
  if (roomBoundaryResult && !roomBoundaryResult.includedRoom) {
    const otherRoom = !includedIsLeft ? path.rightRoom : path.leftRoom;

    assertIsNotNullOrUndefined(otherRoom);

    const otherRoomType = otherRoom.type;
    const specificRoomBorderPositions = roomBoundaryResult.specificRoomBorderPositions;
    const otherRoomBorderPosition = roomBoundaryResult.otherRoomBorderPosition;
    const result = specificOrOtherRoomBorderPositions(
      specificRoomBorderPositions,
      otherRoom,
      otherRoomType,
      otherRoomBorderPosition,
      includedIsLeft,
      true,
    );
    if (result) {
      return result;
    }
  }
  return null;
}

function specificOrOtherRoomBorderPositions(
  specificRoomBorderPositions: RoomBorderPositions | undefined,
  otherRoom: Room,
  otherRoomType: RoomType,
  otherRoomBorderPosition: BorderPosition | undefined,
  includedIsLeft: boolean,
  isExclude: boolean,
) {
  if (specificRoomBorderPositions) {
    for (const border of specificRoomBorderPositions) {
      switch (border.type) {
        case AreaRuleType.BY_ROOM_SELECTOR: {
          if (isRoomSelected(otherRoom, border.roomSelectors)) {
            return edgeOffsetInfoByBorderPosition(border.borderPosition, includedIsLeft, isExclude);
          }
          break;
        }
        case AreaRuleType.BY_ROOM_TYPE: {
          if (border.roomTypes[otherRoomType]) {
            return edgeOffsetInfoByBorderPosition(border.borderPosition, includedIsLeft, isExclude);
          }
          break;
        }
      }
    }
  }

  if (otherRoomBorderPosition) {
    return edgeOffsetInfoByBorderPosition(otherRoomBorderPosition, includedIsLeft, isExclude);
  }

  return null;
}

function edgeOffsetInfoByBorderPosition(
  borderPosition: BorderPosition,
  includedIsLeft: boolean,
  isExclude: boolean,
): EdgeOffsetInfo {
  switch (borderPosition) {
    case BorderPosition.FRAMING_INSIDE:
      return {
        borderOffsetDirLeft: isExclude ? !includedIsLeft : includedIsLeft,
        borderOffsetToFraming: true,
      };
    case BorderPosition.FRAMING_OUTSIDE:
      return {
        borderOffsetDirLeft: isExclude ? includedIsLeft : !includedIsLeft,
        borderOffsetToFraming: true,
      };
    case BorderPosition.CENTERLINE:
      return {
        borderOffsetDirLeft: true,
        borderOffsetToFraming: false,
      };
  }
}
